import { Select, type SelectProps } from '@mantine/core';
import { IconLayersSubtract } from '@tabler/icons-react';
import { useEffect, useRef } from 'react';
import { api } from '~/trpc-extra/api';
import { useProjectId } from '~/hooks/context/useProjectId';

type EnvironmentSelectProps = {
  value: string | null;
  onChange: (value: string | null) => void;
} & Omit<SelectProps, 'data' | 'value' | 'onChange'>;

export function EnvironmentSelect({ value, onChange, ...props }: EnvironmentSelectProps) {
  const projectId = useProjectId();
  const { data, isLoading } = api.userEnvironment.list.useQuery({ projectId: projectId! }, { enabled: !!projectId });

  const environments = data?.environments ?? [];
  const defaultId = data?.defaultId ?? null;

  const options = environments.map((env) => ({
    value: env.id,
    label: env.id === defaultId ? `${env.name} (Default)` : env.name,
  }));

  // Auto-apply: when value is null and a default exists and data just loaded, apply it once
  const hasAutoApplied = useRef(false);
  useEffect(() => {
    if (!hasAutoApplied.current && value === null && defaultId && data) {
      hasAutoApplied.current = true;
      onChange(defaultId);
    }
  }, [value, defaultId, data, onChange]);

  return (
    <Select
      placeholder="No environment"
      leftSection={<IconLayersSubtract size={14} />}
      data={options}
      value={value}
      onChange={onChange}
      clearable
      searchable={options.length > 5}
      disabled={isLoading}
      nothingFoundMessage="No environments found"
      {...props}
    />
  );
}
